import { DOCUMENT } from '@angular/common';
import { Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';
import { ThemeService } from '@campus/features/theming/shared';
import { Store } from '@ngrx/store';
import { EMPTY, catchError, filter, take, timeout } from 'rxjs';
import { feature } from '../+state';
import { ScopedPaletteEntry } from '../models';
import { buildSelectorForScopeKey } from '../utils';

// The directive may ask for a palette before the data has landed in the store
// (e.g. resolver in flight). Give the load a generous window, then give up so
// a never-resolving scope doesn't leak a subscription.
const ENTRY_WAIT_TIMEOUT_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class StyleInjectorService {
  private renderer: Renderer2;
  private document = inject(DOCUMENT);
  private store = inject(Store);
  private themeService = inject(ThemeService);

  // Keyed by `${scopeKey}::${depth}` so the same toc rendered at a different
  // depth (split-tree or deep-link previews) gets its own stylesheet with the
  // correct selector repetition.
  private styleElements = new Map<string, HTMLStyleElement>();
  private pending = new Set<string>();

  constructor() {
    this.renderer = inject(RendererFactory2).createRenderer(null, null);
  }

  public ensureInjected(scopeKey: string, depth = 0): void {
    const cacheKey = makeCacheKey(scopeKey, depth);
    if (this.styleElements.has(cacheKey) || this.pending.has(cacheKey)) return;
    this.pending.add(cacheKey);

    this.store
      .select(feature.selectEntryByScopeKey(scopeKey))
      .pipe(
        filter((entry): entry is ScopedPaletteEntry => !!entry),
        take(1),
        timeout({ first: ENTRY_WAIT_TIMEOUT_MS }),
        catchError(() => EMPTY)
      )
      .subscribe({
        next: (entry) => this.injectForScope(scopeKey, depth, entry),
        complete: () => this.pending.delete(cacheKey),
      });
  }

  private injectForScope(scopeKey: string, depth: number, entry: ScopedPaletteEntry): void {
    const cacheKey = makeCacheKey(scopeKey, depth);
    if (this.styleElements.has(cacheKey)) return;

    const selector = buildSelectorForScopeKey(entry.scopeKey, depth);
    if (!selector) {
      // eslint-disable-next-line no-console
      console.warn('[scoped-palette] unrecognised scope key, cannot build selector:', scopeKey);
      return;
    }

    const css = this.themeService.getStyling(entry.style, selector);
    if (!css) {
      // eslint-disable-next-line no-console
      console.warn('[scoped-palette] empty css for', scopeKey, entry);
      return;
    }

    const styleElement = this.renderer.createElement('style') as HTMLStyleElement;
    this.renderer.setAttribute(styleElement, 'type', 'text/css');
    this.renderer.setAttribute(styleElement, 'data-scoped-palette', scopeKey);
    this.renderer.setAttribute(styleElement, 'data-depth', String(depth));
    this.renderer.setProperty(styleElement, 'textContent', css);
    this.renderer.appendChild(this.document.head, styleElement);

    this.styleElements.set(cacheKey, styleElement);
  }
}

function makeCacheKey(scopeKey: string, depth: number): string {
  return `${scopeKey}::${depth}`;
}
